import type { ApiToken } from "src/queries/auth/tokens.types";
import { SECONDS_PER_DAY } from "src/utils/time/time.constants";
import {
  TOKEN_EXPIRY_WARNING_DAYS,
  TOKEN_EXPIRY_WARNING_LIFETIME_FRACTION,
} from "./TokenExpiryBanner.constants";

export function daysUntilExpiry(expiresAt: string, now: Date = new Date()): number {
  const expires = new Date(expiresAt).getTime();
  const seconds = (expires - now.getTime()) / 1000;
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.max(1, Math.ceil(seconds / SECONDS_PER_DAY));
}

/** Warning window in ms: `warningDays`, capped at a fraction of the token's
 * lifetime so a short-lived token is not flagged the day it is created. */
export function warningWindowMs(
  token: ApiToken,
  warningDays: number = TOKEN_EXPIRY_WARNING_DAYS
): number {
  const fixedMs = warningDays * SECONDS_PER_DAY * 1000;
  const lifetimeMs =
    new Date(token.expires_at).getTime() - new Date(token.created_at).getTime();
  if (!Number.isFinite(lifetimeMs) || lifetimeMs <= 0) return fixedMs;
  return Math.min(fixedMs, lifetimeMs * TOKEN_EXPIRY_WARNING_LIFETIME_FRACTION);
}

export function isTokenExpiringSoon(
  token: ApiToken,
  now: Date = new Date(),
  warningDays: number = TOKEN_EXPIRY_WARNING_DAYS
): boolean {
  if (token.is_revoked) return false;
  const expires = new Date(token.expires_at);
  if (Number.isNaN(expires.getTime()) || expires <= now) return false;
  return expires.getTime() - now.getTime() <= warningWindowMs(token, warningDays);
}

export function getExpiringTokens(
  tokens: ApiToken[] | undefined,
  now: Date = new Date()
): ApiToken[] {
  if (!tokens?.length) return [];
  return tokens
    .filter((token) => isTokenExpiringSoon(token, now))
    .toSorted(
      (a, b) => new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime()
    );
}

export function dismissedKeyFor(tokens: ApiToken[]): string {
  return tokens
    .map((token) => token.jti)
    .toSorted()
    .join(",");
}
